'use client';

import { GhostButton } from '@app/app/_components/GhostButton/GhostButton';
import { Heading } from '@app/app/_components/Heading/Heading';
import { Paragraph } from '@app/app/_components/Paragraph/Paragraph';
import { TextButton } from '@app/app/_components/TextButton/TextButton';
import { RangeSlider } from '@app/app/_components/RangeSlider/RangeSlider';
import { TestChoiceButtonInput } from '@app/app/_components/Input/TestChoiceButtonInput';
import { useAppParameters } from '@app/app/_context/AppParametersContext/useAppParameters';
import { ArrowLeftIcon } from '@app/app/_icons/ArrowLeftIcon';
import { ArrowRightIcon } from '@app/app/_icons/ArrowRightIcon';
import { CloseOutlinedCircleIcon } from '@app/app/_icons/CloseOutlinedCircleIcon';
import { LifeCover } from '@app/domain/model/eshop/life/LifeCover';
import { RangeSliderUnit } from '@app/domain/model/RangeSliderUnit';
import { determineCoverageName } from '@app/infrastructure/policies/determineCoverageName';
import { formatEuroValue } from '@app/infrastructure/application/formatEuroValue';
import { useTranslations } from 'next-intl';
import { ChangeEvent, useCallback, useMemo } from 'react';

interface Props {
    selectedCover: Nullable<LifeCover>;
    selectedCoverPackageSum?: number;
    selectedCoverEnabled?: boolean;
    open: boolean;
    onBack: () => void;
    onClose: () => void;
    showRangeSlider?: boolean;
    showChoiceButtons?: boolean;
    choiceOptions?: number[];
    onValueChange?: (value: number) => void;
    onSwitchChange?: (checked: boolean) => void;
    coverMin?: number;
    coverMax?: number;
    coverStep?: number;
    coverDefault?: number;
}

export function CoverDetailsView({
    selectedCover,
    selectedCoverPackageSum,
    selectedCoverEnabled = false,
    open,
    onBack,
    onClose,
    showRangeSlider = false,
    showChoiceButtons = false,
    choiceOptions = [],
    onValueChange,
    onSwitchChange,
    coverMin = 0,
    coverMax = 0,
    coverStep = 1,
    coverDefault = 0,
}: Props) {
    const { tenant } = useAppParameters();
    const t = useTranslations();
    const parameters = useAppParameters();

    const handleSliderChange = useCallback(
        (value: number) => {
            if (!onValueChange || !onSwitchChange) return;

            if (!selectedCoverEnabled) {
                onSwitchChange(true);
            }

            onValueChange(value);
        },
        [onValueChange, onSwitchChange, selectedCoverEnabled],
    );

    const handleChoiceChange = useCallback(
        (e: ChangeEvent<HTMLInputElement>) => {
            if (!onValueChange || !onSwitchChange) return;

            if (!selectedCoverEnabled) {
                onSwitchChange(true);
            }

            onValueChange(Number(e.target.value));
        },
        [onValueChange, onSwitchChange, selectedCoverEnabled],
    );

    const choiceButtonOptions = useMemo(() => {
        return choiceOptions.map(option => ({
            label: formatEuroValue(option, tenant, true),
            value: String(option),
        }));
    }, [choiceOptions, tenant]);

    const compensationsWithCalculations = useMemo(() => {
        const insuredSum = selectedCoverPackageSum || 0;

        return selectedCover?.compensation.map(element => ({
            accident: element.accident,
            percentage: element.percentage,
            calculatedAmount: (insuredSum * element.percentage) / 100,
        }));
    }, [selectedCover, selectedCoverPackageSum]);

    if (!open || !selectedCover) {
        return null;
    }

    return (
        <>
            <div className="fixed inset-0 z-50 overflow-y-auto bg-neutral-50 md:flex md:items-center md:justify-center md:p-8">
                <div className="flex h-screen w-full flex-col bg-neutral-50 md:min-w-dialog md:max-w-dialog">
                    <div className="mb-4 flex items-center justify-between px-4 py-4">
                        <GhostButton onClick={onBack}>
                            <ArrowLeftIcon className="mr-4 size-8" />
                        </GhostButton>
                        <Heading level="h2" className="flex-1">
                            {t(determineCoverageName(selectedCover.code))}
                        </Heading>
                        <GhostButton onClick={onClose} className="flex-shrink-0">
                            <CloseOutlinedCircleIcon className="size-8" />
                        </GhostButton>
                    </div>

                    <div className="flex-1 px-4">
                        <Paragraph className="mb-8">
                            <span dangerouslySetInnerHTML={{ __html: selectedCover.description ?? '' }} />
                        </Paragraph>

                        {showRangeSlider && onValueChange && onSwitchChange && (
                            <RangeSlider
                                key={`${selectedCover.code}-${selectedCoverEnabled}`}
                                name={`cover_${selectedCover.code}_slider_details`}
                                value={selectedCoverPackageSum ?? coverDefault}
                                defaultValue={coverDefault}
                                min={coverMin}
                                max={coverMax}
                                step={coverStep}
                                unit={RangeSliderUnit.Euro}
                                onChange={handleSliderChange}
                            />
                        )}

                        {showChoiceButtons && onValueChange && onSwitchChange && choiceButtonOptions.length > 0 && (
                            <TestChoiceButtonInput
                                name={`cover_${selectedCover.code}_choice_details`}
                                options={choiceButtonOptions}
                                type="radio"
                                variant="default"
                                value={String(selectedCoverPackageSum ?? coverDefault)}
                                onChange={handleChoiceChange}
                            />
                        )}

                        {selectedCover.compensation.length > 0 && compensationsWithCalculations && (
                            <div className="mt-6">
                                <div className="overflow-x-auto">
                                    <table className="w-full border-collapse">
                                        <thead>
                                            <tr className="border-b border-neutral-200">
                                                <th className="pb-3 pr-4 text-left font-medium">
                                                    {t('eshop.life.issue')}
                                                </th>
                                                <th className="px-4 pb-3 text-right font-medium">
                                                    {t('eshop.life.percentage')}
                                                </th>
                                                <th className="pb-3 pl-4 text-right font-medium">
                                                    {t('eshop.life.compensation.amount')}
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {compensationsWithCalculations.map((compensation, index) => (
                                                <tr key={index} className="border-b border-neutral-200 last:border-b-0">
                                                    <td className="py-3 pr-4">{compensation.accident}</td>
                                                    <td className="px-4 py-3 text-right">{compensation.percentage}%</td>
                                                    <td className="py-3 pl-4 text-right">
                                                        {formatEuroValue(compensation.calculatedAmount, tenant, true)}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}
                        <TextButton
                            preventDefault
                            className="my-6 self-start"
                            rightAddon={<ArrowRightIcon className="size-4" />}
                            href={parameters.eshopLifeInfoUrls.allTerms}
                            target="_blank">
                            {t('eshop.life.allTermsAndConditions')}
                        </TextButton>
                    </div>
                </div>
            </div>
        </>
    );
}
